import type { ReactNode } from 'react';
import { brand, fontFamily, tg } from '../../theme/tokens';

interface InlineCTAProps {
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Primary action button — replacement for Telegram's native MainButton.
 * Floats above BottomNav at fixed bottom: 92px, full-width minus 16px gutters.
 *
 * Use exclusively with `BottomHint` (only one visible at a time per page —
 * hint when no actionable state, CTA when something to do).
 *
 * Disabled state styling intentionally uses muted bg + hint text (not just
 * lower-opacity brand colors) so it visually de-prioritizes the button until
 * the user completes the preceding step. Pair with `BottomHint` to explain
 * what's missing.
 */
export function InlineCTA({ onClick, children, disabled, loading }: InlineCTAProps) {
  const isInteractive = !disabled && !loading;
  return (
    <button
      onClick={isInteractive ? onClick : undefined}
      disabled={!isInteractive}
      style={{
        position: 'fixed',
        bottom: 92,
        left: 16,
        right: 16,
        height: 52,
        borderRadius: 16,
        background: isInteractive ? brand.gradient : tg.bg2,
        color: isInteractive ? '#fff' : tg.textHint,
        fontSize: 16,
        fontWeight: 700,
        fontFamily,
        border: 'none',
        boxShadow: isInteractive ? brand.glow : 'none',
        cursor: isInteractive ? 'pointer' : 'not-allowed',
        appearance: 'none',
        zIndex: 49,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        transition: 'background 200ms, box-shadow 200ms',
      }}
    >
      {loading && (
        <span
          aria-hidden
          style={{
            width: 16,
            height: 16,
            borderRadius: 999,
            border: '2px solid rgba(255,255,255,0.3)',
            borderTopColor: '#fff',
            animation: 'dk-cta-spin 0.9s linear infinite',
          }}
        />
      )}
      {children}
      <style>{`
        @keyframes dk-cta-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
}
